import { ArticleTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ArticleTransformer';
import { GraphqlArticle, GraphqlCompetition } from '@jbhi-fi/apis/src/contentful/gqlTypes';
import { SEARCH_CONTENT_SUGGESTIONS_GQL } from '@jbhi-fi/apis/src/contentful/gqlQueries';
import { mapArticleEntry, mapCompetitionEntry } from '@jbhi-fi/apis/src/contentful/gqlUtils';
import { CompetitionTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CompetitionTransformer';
import { getIsoDateXMonthsAgo, getLocalTimeWithoutTimezone } from '@jbhi-fi/utils/src/date';
import { ContentfulApiClient } from '../../api/contentful/ContentfulClient';
import { serviceHelper } from '../../config/service-helper';
import { MiniCardProps } from '@jbhi-fi/components/src/MiniCard/MiniCard';

const MAX_SEARCH_RESULTS = 6;

const contentfulClient = new ContentfulApiClient();

type SearchSuggestionsGqlResponse = {
  data?: {
    articleCollection?: { items?: Array<GraphqlArticle | null> | null } | null;
    competitionCollection?: { items?: Array<GraphqlCompetition | null> | null } | null;
  } | null;
};

export const getSearchContentSuggestions = async (searchTerm: string): Promise<Array<MiniCardProps>> => {
  const articleMonthsToSearchBack = window.Jbhifi?.contentfulSettings?.articleContentfulMonthsToSearchBack ?? 12;

  const gqlResponse = await contentfulClient.getGql<SearchSuggestionsGqlResponse>(SEARCH_CONTENT_SUGGESTIONS_GQL, {
    searchTerm,
    articleLimit: MAX_SEARCH_RESULTS,
    competitionLimit: MAX_SEARCH_RESULTS,
    publishedDateGte: getIsoDateXMonthsAgo(articleMonthsToSearchBack),
    competitionEndDateGte: getLocalTimeWithoutTimezone(new Date()),
  });

  const gqlArticleItems = (gqlResponse.data?.data?.articleCollection?.items || []).filter(
    (item): item is GraphqlArticle => !!item
  );
  const gqlCompetitionItems = (gqlResponse.data?.data?.competitionCollection?.items || []).filter(
    (item): item is GraphqlCompetition => !!item
  );

  const articleEntries = gqlArticleItems.map(item => mapArticleEntry(item, serviceHelper.contentfulSpaceId));
  const competitionEntries = gqlCompetitionItems.map(item =>
    mapCompetitionEntry(item, serviceHelper.contentfulSpaceId)
  );

  const uniqueSortedArticleEntries = articleEntries.sort(
    (a, b) =>
      new Date(b.fields?.publishedDate ?? b.sys.createdAt ?? 0).getTime() -
      new Date(a.fields?.publishedDate ?? a.sys.createdAt ?? 0).getTime()
  );

  const transformedArticles = uniqueSortedArticleEntries.map(item => ({
    ...ArticleTransformer.toMiniCardProps(item),
    searchTerm,
  }));

  const transformedCompetitions = competitionEntries.map(item => ({
    ...CompetitionTransformer.toMiniCardProps(item),
    searchTerm,
  }));

  return [...transformedCompetitions, ...transformedArticles].slice(0, MAX_SEARCH_RESULTS);
};
